import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, LogOut, Eye, EyeOff, MonitorPlay } from "lucide-react";
import { api, formatError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Brand } from "@/components/Brand";
import { CameraForm } from "@/components/CameraForm";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const CamRow = ({ c, onEdit, onDelete }) => (
  <li className="admin-row" data-testid={`admin-camera-row-${c.id}`}>
    <div className="min-w-0 flex-1">
      <div className="flex items-center gap-2">
        <h3 className="cam-name">{c.name}</h3>
        <span className="mono tag">{c.stream_type.toUpperCase()}</span>
        {c.enabled ? <Eye size={14} className="text-[var(--ok)]" /> : <EyeOff size={14} className="text-[var(--muted)]" />}
      </div>
      <p className="mono url-line">{c.url}</p>
      {c.location && <p className="cam-loc">{c.location}</p>}
    </div>
    <div className="flex gap-2">
      <button className="icon-btn" onClick={() => onEdit(c)} data-testid={`edit-camera-${c.id}`}><Pencil size={15} /></button>
      <button className="icon-btn danger" onClick={() => onDelete(c)} data-testid={`delete-camera-${c.id}`}><Trash2 size={15} /></button>
    </div>
  </li>
);

export default function Admin() {
  const { user, logout } = useAuth();
  const [cams, setCams] = useState([]);
  const [form, setForm] = useState({ open: false, initial: null });
  const [saving, setSaving] = useState(false);
  const [toDelete, setToDelete] = useState(null);

  const load = () => api.get("/admin/cameras").then((r) => setCams(r.data)).catch((e) => toast.error(formatError(e)));
  useEffect(() => { load(); }, []);

  const save = async (data) => {
    setSaving(true);
    try {
      if (form.initial) await api.put(`/admin/cameras/${form.initial.id}`, data);
      else await api.post("/admin/cameras", data);
      toast.success("Câmera salva");
      setForm({ open: false, initial: null });
      load();
    } catch (e) { toast.error(formatError(e)); }
    setSaving(false);
  };

  const remove = async () => {
    try { await api.delete(`/admin/cameras/${toDelete.id}`); toast.success("Câmera removida"); load(); }
    catch (e) { toast.error(formatError(e)); }
    setToDelete(null);
  };

  return (
    <div className="page">
      <header className="topbar">
        <Brand />
        <div className="topbar-right">
          <span className="mono meta hidden sm:inline" data-testid="admin-username">{user?.username}</span>
          <Link to="/" className="btn-ghost" data-testid="view-panel-link"><MonitorPlay size={14} /> Painel</Link>
          <button className="btn-ghost" onClick={logout} data-testid="logout-button"><LogOut size={14} /> Sair</button>
        </div>
      </header>
      <section className="hero flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="mono eyebrow">// administração</p>
          <h1 className="title">Câmeras</h1>
          <p className="mono meta">{cams.length} cadastrada(s)</p>
        </div>
        <button className="btn-primary" onClick={() => setForm({ open: true, initial: null })} data-testid="add-camera-button">
          <Plus size={16} /> Adicionar câmera
        </button>
      </section>
      <ul className="admin-list" data-testid="admin-camera-list">
        {cams.map((c) => <CamRow key={c.id} c={c} onEdit={(i) => setForm({ open: true, initial: i })} onDelete={setToDelete} />)}
        {cams.length === 0 && <li className="empty" data-testid="admin-empty">Nenhuma câmera. Clique em “Adicionar câmera”.</li>}
      </ul>
      <CameraForm open={form.open} initial={form.initial} saving={saving}
        onClose={() => setForm({ open: false, initial: null })} onSave={save} />
      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent className="vl-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Remover câmera?</AlertDialogTitle>
            <AlertDialogDescription>“{toDelete?.name}” será removida do painel.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-cancel">Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={remove} data-testid="delete-confirm">Remover</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
